import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { Child, LastResults } from '@isophi/pap-api';
import {
  AuthService,
  LicensePapFeature,
  LicenseProductType,
  LicenseService as GeneralLicenseService,
  LinksService,
} from '@isophi/core-legacy';
import { combineLatest, Observable, finalize, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ChildService } from '@app/core/services/child.service';
import { LicenseService } from '../../../core/services/license.service';
import { environment } from '../../../../environments/environment';
import { KindergartenService } from '@app/core/services/kindergarten.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SwitchDemoKindergartenService } from '@isophi/mng-api';
import { ResultService } from '@app/core/services/result.service';

enum ExternalApps {
  TEACHER,
  MANAGEMENT,
  CHILD_PORTFOLIO,
}

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MenuComponent implements OnDestroy {
  private readonly childService = inject(ChildService);
  private readonly licenseService = inject(LicenseService);
  private readonly generalLicenseService = inject(GeneralLicenseService);
  private readonly authService = inject(AuthService);
  private readonly kindergartenService = inject(KindergartenService);
  private readonly switchDemoKindergartenService = inject(SwitchDemoKindergartenService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly resultService = inject(ResultService);
  protected readonly linkService = inject(LinksService);

  protected readonly teachersResults$ = this.resultService.allTeacherResults$;
  protected readonly selectedResult$ = this.resultService.selectedResult$;
  private readonly ngUnsubscribe$ = new Subject<void>();

  // license-driven visibility streams
  readonly showDemoKindergarten$ = this.generalLicenseService.canSeeDemoKindergarten;
  readonly showTeacherApp$ = this.generalLicenseService.canSeeTeacherApp;
  readonly showManagementApp$ = this.generalLicenseService.canSeeManagement;
  readonly showChildPortfolio$ = this.generalLicenseService.canSeeChildPortfolio;
  readonly papLocked$ = combineLatest([
    this.generalLicenseService.canSeePap,
    this.generalLicenseService.isModuleLocked(LicenseProductType.PAP, LicensePapFeature.ACCESS_PAP, LicensePapFeature.TRIAL),
  ])
    .pipe(map(([canSeePap, isPapExpired]) => isPapExpired || !canSeePap))
    .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

  // derived stream for demo switch visibility
  readonly showDemoSwitch$ = this.showDemoKindergarten$.pipe(
    map((showDemoLicense) => !this.kindergartenService.loggedKindergarten?.isDemo && showDemoLicense)
  );

  licenses$: Observable<{ premium: boolean }>;
  selectedChild$: Observable<Child | null>;
  results$: Observable<LastResults | null>;

  externalApps = ExternalApps;
  imageSrcTeacher = 'assets/img/logo/isophi-diag_wheel_logo_256.png';
  imageSrcManagement = 'assets/img/logo/isophi-mng_wheel_logo_256.png';
  imageSrcChildPortfolio = 'assets/img/logo/isophi-mng_wheel_logo_256.png';
  protected readonly teacherAppUrl = this.linkService.getTeacherAppUrl();
  protected readonly mngAppUrl = this.linkService.getManagementAppUrl();
  protected readonly childrenPortfolioUrl = this.linkService.getManagementAppUrl({ redirect: '/children-portfolio/dashboard' });

  constructor() {
    this.licenses$ = combineLatest({
      premium: this.licenseService.hasPremium(),
    });
    this.selectedChild$ = this.childService.getSelectedChild();
    this.results$ = this.childService.getSelectedChildResults();
  }

  get isTeacher(): boolean {
    return this.authService.loggedUser?.teacher?.kindergartenIds.length > 0;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  changeImage(newSrc: string, typeOfApp: ExternalApps): void {
    if (this.externalApps.TEACHER === typeOfApp) {
      this.imageSrcTeacher = newSrc;
    } else if (this.externalApps.MANAGEMENT === typeOfApp) {
      this.imageSrcManagement = newSrc;
    } else if (this.externalApps.CHILD_PORTFOLIO === typeOfApp) {
      this.imageSrcChildPortfolio = newSrc;
    }
  }

  switchDemoToDemoKindergarten(): void {
    this.spinner.show();
    this.switchDemoKindergartenService
      .switchDemoKindergartenRetrieve(environment.client_id)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe((response) => {
        const url = new URL(response.redirectUrl);
        if (!this.kindergartenService.loggedKindergarten.isDemo) {
          url.searchParams.append('showDemoPopup', 'true');
          url.searchParams.append('originUserUuid', this.authService.loggedUser.uuid);
        }

        const a = document.createElement('a');
        a.href = url.toString();
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  }
}
